import {Trans} from '@lingui/macro';
import React, {FC} from 'react';
import styled from 'styled-components';

import {Button} from '../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {ChromelessButton} from '../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {IconButton} from '../../../../libs/shared/ui/src/components/buttons/iconButton';
import {Icon} from '../../../../libs/shared/ui/src/components/icon/icon';
import {VisualSizesEnum} from '../../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {textSelection} from '../../../../libs/shared/ui/src/styles/mixins';
import {useUserTheme} from '../../../../libs/shared/ui/src/theme/userThemeProvider';
import {useAppDispatch, useAppSelector} from '../../../helpers/reduxHelpers';
import {makeSettingsUrlFromPathId} from '../../../settings/menu/settingsHelpers';
import {dismissDefaultConferencingBannerAction} from '../../../store/data/localPreferences/localPreferencesActions';
import {getUser} from '../../../store/data/teammates/teammatesSelectors';
import {GridAreaItem} from '../../common/grid';
import {useLinkNavigation} from '../../common/navigation/useLinkNavigation';

/*
 * Props.
 */

interface DefaultVideoConferencingBannerProps {
  className?: string;
}

/*
 * Style.
 */

const StyledInfoBoxDiv = styled.div`
  flex-shrink: 0;
  position: relative;
  display: grid;
  grid-template-areas:
    'icon body .'
    '. footer .';
  grid-template-columns: 24px 1fr 34px;
  grid-template-rows: repeat(2, auto);
  grid-row-gap: 4px;
  width: 100%;
  height: 100%;
  border-radius: 8px;
  background: ${(p) => p.theme.palette.blue.shade20};
  padding: 8px 3px 8px 8px;
  ${textSelection()}
`;

const StyledBodyDiv = styled(GridAreaItem).attrs({
  area: 'body',
})`
  color: ${(p) => p.theme.greys.shade80};
  font-size: ${fontSizes.verySmall};
  font-weight: ${fontWeights.normal};
  line-height: 16px;
`;

const StyledFooterDiv = styled(GridAreaItem).attrs({
  area: 'footer',
})`
  display: flex;
`;

const StyledIconWrapper = styled(ChromelessButton)`
  grid-area: icon;
  color: ${(p) => p.theme.greys.shade80};
`;

const StyledButtonContentDiv = styled.div`
  display: grid;
  grid-auto-flow: column;
  grid-template-columns: auto;
  grid-column-gap: 4px;
  font-size: ${fontSizes.small};
  font-weight: ${fontWeights.semibold};
`;

const StyledButton = styled(Button)`
  margin-left: -12px;
`;

const StyledCloseButtonContainerDiv = styled.div`
  position: absolute;
  right: 4px;
  top: 4px;
`;

/*
 * Component.
 */

export const DefaultVideoConferencingBanner: FC<DefaultVideoConferencingBannerProps> = (props) => {
  const theme = useUserTheme();
  const {navigateToLink} = useLinkNavigation();
  const dispatch = useAppDispatch();
  const dismissDefaultConferencingBanner = () => dispatch(dismissDefaultConferencingBannerAction());
  const user = useAppSelector(getUser);

  return (
    <StyledInfoBoxDiv className={props.className}>
      <StyledIconWrapper>
        <Icon name="infoFilledSmall" color={theme.palette.blue.shade40} />
      </StyledIconWrapper>
      <StyledBodyDiv>
        <Trans id="default-conferencing-banner-text">
          You can set your <b>default video conference</b> in settings.
        </Trans>
      </StyledBodyDiv>
      <StyledCloseButtonContainerDiv>
        <IconButton
          label={<Trans id="default-conferencing-banner-close">Close</Trans>}
          placement="bottom-end"
          size={VisualSizesEnum.SMALL}
          onClick={dismissDefaultConferencingBanner}
          iconName="closeSmall"
          color={theme.greys.shade70}
          hoverColor={theme.greys.shade80}
        />
      </StyledCloseButtonContainerDiv>
      <StyledFooterDiv>
        <StyledButton
          size={VisualSizesEnum.SMALL}
          variant={ButtonVariantsEnum.LINK}
          onClick={() => {
            const preferencesUrl = makeSettingsUrlFromPathId(
              'settings.myPreferences',
              {
                namespace: user.namespace,
              },
              {search: 'Default video conference'},
            );
            navigateToLink(preferencesUrl);
            dismissDefaultConferencingBanner();
          }}
        >
          <StyledButtonContentDiv>
            <Trans id="default-conferencing-banner-button">Go to settings</Trans>
            <Icon name="externalLinkSmall" />
          </StyledButtonContentDiv>
        </StyledButton>
      </StyledFooterDiv>
    </StyledInfoBoxDiv>
  );
};
